import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { CUSTOMER_ROUTES, CUSTOMER_WORKSPACES, CUSTOMER_UNGROUPED_ROUTES, can } from '@cvsi/shared';
import { useTenant } from '../lib/tenantContext';

/** Role-aware workspace links and dropdowns in the top application bar. */
export function TopNavigation() {
  const { context, companyName, signOut } = useTenant();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!context) return null;

  const byPath = new Map(CUSTOMER_ROUTES.map((route) => [route.path, route]));
  const isAllowed = (path: string) => {
    const route = byPath.get(path);
    return route != null && (route.scope == null || can(context.role, route.scope));
  };
  const renderLink = (path: string) => {
    const route = byPath.get(path);
    if (!route || !isAllowed(path)) return null;
    return (
      <NavLink
        key={route.path}
        to={route.path}
        className={({ isActive }) => (isActive ? 'active' : '')}
        onClick={() => {
          setOpenMenu(null);
          setMobileOpen(false);
        }}
      >
        {route.label}
      </NavLink>
    );
  };

  const groups = CUSTOMER_WORKSPACES.filter((group) => group.paths.some(isAllowed));
  const morePaths = CUSTOMER_UNGROUPED_ROUTES.filter(isAllowed);
  const toggleMenu = (label: string) => setOpenMenu((current) => (current === label ? null : label));

  return (
    <header className="top-navigation">
      <Link className="portal-brand" to="/dashboard" aria-label={`${companyName ?? 'CVSI'} customer portal home`}>
        <span className="portal-logo-crop"><img src="/cvsi-logo.png" alt="CVSI by Cruze Rideshare Inc." /></span>
        <span className="portal-brand-copy">
          <strong>{companyName ?? 'CVSI'}</strong>
          <small>Customer portal</small>
        </span>
      </Link>

      <button
        type="button"
        className="mobile-nav-toggle"
        aria-expanded={mobileOpen}
        aria-controls="customer-portal-navigation"
        aria-label={mobileOpen ? 'Close portal navigation' : 'Open portal navigation'}
        onClick={() => {
          setMobileOpen((current) => !current);
          setOpenMenu(null);
        }}
      >
        <span aria-hidden="true">{mobileOpen ? '×' : '☰'}</span>
        <span>{mobileOpen ? 'Close' : 'Menu'}</span>
      </button>

      <nav
        id="customer-portal-navigation"
        className={`top-nav-groups${mobileOpen ? ' is-open' : ''}`}
        aria-label="Customer portal workspaces"
      >
        {groups.map((group) => {
          const allowedPaths = group.paths.filter(isAllowed);
          if (allowedPaths.length === 1) {
            return (
              <div className={`workspace-menu workspace-menu--${group.tone}`} key={group.label}>
                <NavLink
                  to={allowedPaths[0]}
                  className={({ isActive }) => `workspace-trigger workspace-direct-link${isActive ? ' active' : ''}`}
                  onClick={() => {
                    setOpenMenu(null);
                    setMobileOpen(false);
                  }}
                >
                  <span className="workspace-trigger-label"><span className="workspace-trigger-dot" aria-hidden="true" />{group.label}</span>
                </NavLink>
              </div>
            );
          }
          const open = openMenu === group.label;
          const menuId = `workspace-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          const menuClass = `workspace-menu workspace-menu--${group.tone}`;
          return (
            <div className={menuClass} key={group.label}>
              <button
                type="button"
                className="workspace-trigger"
                aria-expanded={open}
                aria-controls={menuId}
                onClick={() => toggleMenu(group.label)}
              >
                <span className="workspace-trigger-label"><span className="workspace-trigger-dot" aria-hidden="true" />{group.label}</span>
                <span aria-hidden="true">{open ? '▴' : '▾'}</span>
              </button>
              {open && (
                <div id={menuId} className="workspace-dropdown">
                  <div className="workspace-dropdown-heading">
                    <strong>{group.label}</strong>
                    <span>{group.description}</span>
                  </div>
                  {group.paths.map(renderLink)}
                </div>
              )}
            </div>
          );
        })}

        <div className="workspace-menu utilities-menu">
          <button
            type="button"
            className="workspace-trigger"
            aria-expanded={openMenu === 'More'}
            aria-controls="workspace-more"
            onClick={() => toggleMenu('More')}
          >
            More <span aria-hidden="true">{openMenu === 'More' ? '▴' : '▾'}</span>
          </button>
          {openMenu === 'More' && (
            <div id="workspace-more" className="workspace-dropdown">
              {morePaths.map(renderLink)}
              <a href="https://cruzenc.com/" onClick={() => setMobileOpen(false)}>Back to Website</a>
              <button type="button" className="workspace-signout" onClick={signOut}>Sign out</button>
            </div>
          )}
        </div>
      </nav>

      {(openMenu || mobileOpen) && (
        <button
          className="workspace-scrim"
          aria-label="Close navigation options"
          onClick={() => {
            setOpenMenu(null);
            setMobileOpen(false);
          }}
        />
      )}
    </header>
  );
}
